import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 环境科学 - 横屏「气候变化与碳排放」风格
 *
 * 核心亮点：
 * - SVG 温度曲线图（折线图 + 区域填充，动画绘制）
 * - 碳排放条形图（动画增长柱状图）
 * - 生态系统指标（带图标）
 * - 气候数据统计卡片
 * - 底部关键事实
 *
 * 尺寸：1920x1080 横屏
 */

interface TempDataPoint {
  year: number;
  temp: number;
}

interface EmissionSource {
  source: string;
  value: number;
  color: string;
}

interface EcosystemStat {
  label: string;
  value: string;
  icon: string;
  status: 'good' | 'warning' | 'critical';
}

interface EnvironmentClimateTemplateProps {
  topic: string;
  subtitle: string;
  tempData: TempDataPoint[];
  emissions: EmissionSource[];
  ecosystemStats: EcosystemStat[];
  keyFact: string;
  actions: string[];
}

const DEFAULT_PROPS: EnvironmentClimateTemplateProps = {
  topic: '全球气候变化与碳排放',
  subtitle: 'Global Climate Change & Carbon Emissions',
  tempData: [
    { year: 1960, temp: 13.9 },
    { year: 1965, temp: 13.92 },
    { year: 1970, temp: 13.95 },
    { year: 1975, temp: 13.98 },
    { year: 1980, temp: 14.05 },
    { year: 1985, temp: 14.12 },
    { year: 1990, temp: 14.25 },
    { year: 1995, temp: 14.38 },
    { year: 2000, temp: 14.48 },
    { year: 2005, temp: 14.58 },
    { year: 2010, temp: 14.65 },
    { year: 2015, temp: 14.72 },
    { year: 2020, temp: 14.8 },
  ],
  emissions: [
    { source: '能源', value: 72, color: '#ef4444' },
    { source: '工业', value: 21, color: '#f59e0b' },
    { source: '交通', value: 16, color: '#3b82f6' },
    { source: '农业', value: 12, color: '#22c55e' },
  ],
  ecosystemStats: [
    { label: '海平面上升', value: '3.3mm/年', icon: '🌊', status: 'critical' },
    { label: '北极冰盖', value: '-13%/十年', icon: '🧊', status: 'critical' },
    { label: '森林覆盖率', value: '31%', icon: '🌲', status: 'warning' },
    { label: '可再生能源', value: '29%', icon: '☀️', status: 'good' },
  ],
  keyFact: '过去 60 年全球平均气温上升约 0.9°C，2020 年是有记录以来最暖的年份之一',
  actions: [
    '减少化石能源使用，转向清洁能源',
    '提高能源效率，降低工业排放',
    '绿色出行，优先公共交通',
    '减少食物浪费，支持可持续农业',
  ],
};

export const EnvironmentClimateTemplate: React.FC<
  Partial<EnvironmentClimateTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });
  const tempChartDraw = interpolate(frame, [25, 100], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const emissionStart = 110;
  const emissionInterval = 25;
  const ecosystemStart = emissionStart + p.emissions.length * emissionInterval + 30;
  const factStart = ecosystemStart + 100;
  const factOpacity = interpolate(frame, [factStart, factStart + 25], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const actionsStart = factStart + 50;

  const chartPad = { left: 65, right: 40, top: 16, bottom: 40 };
  const svgW = 880;
  const chartW = svgW - chartPad.left - chartPad.right;
  const chartH = 240;
  const chartY = chartPad.top;

  const minTemp = Math.min(...p.tempData.map((d) => d.temp));
  const maxTemp = Math.max(...p.tempData.map((d) => d.temp));
  const tempRange = maxTemp - minTemp || 1;
  const maxEmission = Math.max(...p.emissions.map((e) => e.value));

  const tempToY = (t: number) =>
    chartY + chartH - ((t - minTemp) / tempRange) * chartH;
  const yearToX = (i: number) =>
    chartPad.left + (i / (p.tempData.length - 1)) * chartW;

  const linePoints = p.tempData.map((d, i) => `${yearToX(i)},${tempToY(d.temp)}`);
  const linePath = `M${linePoints.join(' L')}`;
  const areaPath = `${linePath} L${yearToX(p.tempData.length - 1)},${chartY + chartH} L${yearToX(0)},${chartY + chartH} Z`;

  const PATH_LENGTH = 100;
  const strokeDashoffset = PATH_LENGTH * (1 - tempChartDraw);

  const statusColors = {
    good: '#22c55e',
    warning: '#f59e0b',
    critical: '#ef4444',
  };

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0a1a0f 0%, #0f2a1a 50%, #0a1a0f 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Subtle grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(34,197,94,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(34,197,94,0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 50,
          right: 50,
          opacity: headerProgress,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <div
            style={{
              padding: '5px 14px',
              background: '#22c55e15',
              border: '1px solid #22c55e35',
              borderRadius: 8,
              fontSize: 22,
              color: '#22c55e',
              fontWeight: 700,
            }}
          >
            🌍 环境科学
          </div>
          <h1 style={{ fontSize: 36, fontWeight: 900, margin: 0 }}>{p.topic}</h1>
          <p style={{ fontSize: 26, color: 'rgba(255,255,255,0.35)', margin: 0 }}>
            {p.subtitle}
          </p>
        </div>
      </div>

      {/* ========== 左列：温度曲线图 + 行动建议 ========== */}
      <div
        style={{
          position: 'absolute',
          top: 180,
          left: 30,
          width: 960,
          bottom: 80,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 温度曲线图 */}
        <div>
          <div
            style={{
              fontSize: 26,
              color: 'rgba(255,255,255,0.4)',
              marginBottom: 30,
              fontWeight: 600,
              paddingLeft: 20,
            }}
          >
            全球年均气温变化 (°C)
          </div>
          <svg
            width={svgW}
            height={chartH + 50}
            viewBox={`0 0 ${svgW} ${chartH + 50}`}
          >
            <defs>
              <linearGradient id="tempGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22c55e" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((t) => {
              const y = chartY + (1 - t) * chartH;
              const tempVal = (minTemp + t * tempRange).toFixed(1);
              return (
                <g key={t}>
                  <line
                    x1={chartPad.left}
                    y1={y}
                    x2={chartPad.left + chartW}
                    y2={y}
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth="1"
                  />
                  <text
                    x={chartPad.left - 8}
                    y={y + 4}
                    fill="rgba(255,255,255,0.3)"
                    fontSize="20"
                    textAnchor="end"
                  >
                    {tempVal}
                  </text>
                </g>
              );
            })}
            <clipPath id="tempClip">
              <rect
                x={chartPad.left}
                y={chartY}
                width={chartW * tempChartDraw}
                height={chartH}
              />
            </clipPath>
            <path
              d={areaPath}
              fill="url(#tempGrad)"
              clipPath="url(#tempClip)"
            />
            <path
              d={linePath}
              fill="none"
              stroke="#22c55e"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              pathLength={PATH_LENGTH}
              strokeDasharray={PATH_LENGTH}
              strokeDashoffset={strokeDashoffset}
            />
            {p.tempData
              .filter((_, i) => i % 2 === 0)
              .map((d) => {
                const idx = p.tempData.indexOf(d);
                const x = yearToX(idx);
                return (
                  <text
                    key={d.year}
                    x={x}
                    y={chartY + chartH + 24}
                    fill="rgba(255,255,255,0.35)"
                    fontSize="20"
                    textAnchor="middle"
                  >
                    {d.year}
                  </text>
                );
              })}
          </svg>
        </div>

        {/* 行动建议 */}
        <div style={{ marginTop: 30, paddingLeft: 20 }}>
          <div
            style={{
              fontSize: 26,
              color: 'rgba(255,255,255,0.4)',
              marginBottom: 20,
              fontWeight: 600,
            }}
          >
            🌱 我们可以做什么
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {p.actions.map((action, i) => {
              const opacity = interpolate(
                frame,
                [actionsStart + i * 20, actionsStart + i * 20 + 18],
                [0, 1],
                { extrapolateRight: 'clamp' }
              );
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    opacity,
                    padding: '6px 14px',
                    background: 'rgba(34,197,94,0.06)',
                    borderRadius: 8,
                    border: '1px solid rgba(34,197,94,0.12)',
                  }}
                >
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: '#22c55e',
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 22,
                      color: 'rgba(255,255,255,0.7)',
                      lineHeight: 1.4,
                    }}
                  >
                    {action}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ========== 右列：碳排放 + 生态系统指标 ========== */}
      <div
        style={{
          position: 'absolute',
          top: 180,
          left: 1020,
          right: 50,
          bottom: 80,
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        }}
      >
        {/* 碳排放条形图 */}
        <div>
          <div
            style={{
              fontSize: 26,
              color: 'rgba(255,255,255,0.4)',
              marginBottom: 12,
              fontWeight: 600,
            }}
          >
            碳排放来源占比 (%)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {p.emissions.map((em, i) => {
              const start = emissionStart + i * emissionInterval;
              const barProgress = spring({
                frame: frame - start,
                fps,
                from: 0,
                to: 1,
                config: { damping: 14, stiffness: 120 },
              });
              const widthPct = (em.value / maxEmission) * 100;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 56,
                      fontSize: 22,
                      color: 'rgba(255,255,255,0.8)',
                      flexShrink: 0,
                    }}
                  >
                    {em.source}
                  </div>
                  <div
                    style={{
                      flex: 1,
                      height: 24,
                      background: 'rgba(255,255,255,0.06)',
                      borderRadius: 6,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${widthPct * barProgress}%`,
                        height: '100%',
                        background: em.color,
                        borderRadius: 6,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      width: 48,
                      fontSize: 18,
                      fontWeight: 700,
                      color: em.color,
                      textAlign: 'right',
                    }}
                  >
                    {em.value}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 生态系统指标 */}
        <div>
          <div
            style={{
              fontSize: 26,
              color: 'rgba(255,255,255,0.4)',
              marginBottom: 10,
              marginTop: 20,
              fontWeight: 600,
            }}
          >
            生态系统指标
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {p.ecosystemStats.map((stat, i) => {
              const progress = spring({
                frame: frame - ecosystemStart - i * 15,
                fps,
                from: 0,
                to: 1,
                config: { damping: 12, stiffness: 140 },
              });
              const statusColor = statusColors[stat.status];
              return (
                <div
                  key={i}
                  style={{
                    padding: '14px 16px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: 12,
                    border: `1px solid ${statusColor}25`,
                    opacity: progress,
                    transform: `translateY(${(1 - progress) * 10}px)`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ fontSize: 24 }}>{stat.icon}</span>
                    <span
                      style={{
                        fontSize: 24,
                        color: 'rgba(255,255,255,0.8)',
                        fontWeight: 600,
                      }}
                    >
                      {stat.label}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 800,
                      color: statusColor,
                    }}
                  >
                    {stat.value}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ========== 关键事实 ========== */}
      <div
        style={{
          position: 'absolute',
          bottom: 30,
          left: 50,
          right: 50,
          opacity: factOpacity,
        }}
      >
        <div
          style={{
            padding: '14px 24px',
            background: 'rgba(10,26,15,0.9)',
            border: '1px solid rgba(34,197,94,0.25)',
            borderRadius: 12,
            textAlign: 'center',
          }}
        >
          <span
            style={{
              fontSize: 22,
              color: 'rgba(255,255,255,0.9)',
              lineHeight: 1.5,
            }}
          >
            🌡️ {p.keyFact}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #22c55e, #16a34a)',
        }}
      />
    </AbsoluteFill>
  );
};
